import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import { SDialog } from '@modules/ui-components/dialogs/SDialog';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import type { CliCandidateRecord, CliCandidateStep } from '@modules/shared-types';
import type { ExperimentDocument } from '../experimentTypes';
import { ExperimentsApiClient, ExperimentsApiError } from '../api/experimentsClient';
import { EXPERIMENTS_TEST_IDS } from '../experimentsTestIds';

/**
 * Spec D4 (§5.1) — the candidate review block on the experiment card: one
 * button turns a report into a reviewable corrected version. The block POLLS
 * the candidate record while 'preparing' (the pipeline persists per-step
 * progress server-side), discloses replaced manual polish (G1), derives
 * staleness from the lineage at render time (G2 client half — the server
 * re-validates at exposure), and gates Publish / Run A/B behind an explicit
 * P3 confirm that calls confirm-exposure FIRST.
 *
 * Stop-valve (AC-D4-5): when the report has a measurement problem the whole
 * block renders NOTHING — the prepare affordance is absent, not disabled.
 */

const POLL_INTERVAL_MS = 3_000;

/** Operator-facing step vocabulary (mock 1) — no pipeline jargon. */
const STEP_COPY: Array<{ key: CliCandidateStep; doing: string; done: string }> = [
  { key: 'gate', doing: 'Checking report gates', done: 'Checked report gates' },
  { key: 'absorb', doing: 'Saving your onboarding edits into the strategy', done: 'Saved your onboarding edits into the strategy' },
  { key: 'theme-sync', doing: 'Matching theme to the live version', done: 'Matched theme to the live version' },
  { key: 'weights', doing: 'Updating actor weights', done: 'Updated actor weights' },
  { key: 'recompute', doing: 'Recomputing insights', done: 'Recomputed insights' },
  { key: 'diff', doing: 'Measuring impact', done: 'Measured impact' },
  { key: 'regen', doing: 'Rewriting affected blocks', done: 'Rewrote affected blocks' },
  { key: 'compile', doing: 'Building the new onboarding version', done: 'Built the new onboarding version' },
];

interface CandidateReviewBlockProps {
  projectId: string;
  experiment: ExperimentDocument;
  client: ExperimentsApiClient;
  /** P6 stop valve — measurement is broken, candidate affordances must vanish. */
  stopValve: boolean;
  /** Basis analysis for a first prepare (from interpretation deep links); null → no prepare affordance. */
  fromAnalysisId: string | null;
}

export const CandidateReviewBlock: React.FC<CandidateReviewBlockProps> = ({
  projectId,
  experiment,
  client,
  stopValve,
  fromAnalysisId,
}) => {
  const [candidate, setCandidate] = useState<CliCandidateRecord | null>(experiment.candidate ?? null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stale, setStale] = useState(false);
  const [confirm, setConfirm] = useState<'publish' | 'run-ab' | null>(null);
  const navigate = useNavigate();
  const pollRef = useRef<number | null>(null);

  // The hub refetch may deliver a fresher document than our local copy.
  useEffect(() => {
    if (experiment.candidate) setCandidate(experiment.candidate);
  }, [experiment.candidate, experiment.updatedAt]);

  // Poll the record while the detached pipeline runs (it persists every step).
  useEffect(() => {
    if (candidate?.status !== 'preparing') return undefined;
    const tick = async () => {
      try {
        const data = await client.getCandidate(projectId, experiment.id);
        if (data.candidate) setCandidate(data.candidate);
      } catch {
        // transient — keep polling; terminal failures land in candidate.error
      }
    };
    pollRef.current = window.setInterval(tick, POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) window.clearInterval(pollRef.current);
    };
  }, [candidate?.status, client, projectId, experiment.id]);

  // G2 client half: a ready candidate built on a superseded lineage node is stale.
  useEffect(() => {
    if (candidate?.status !== 'ready') {
      setStale(false);
      return undefined;
    }
    const basisId = candidate.basis.toAnalysisId || candidate.basis.fromAnalysisId;
    if (!basisId) return undefined;
    const controller = new AbortController();
    client.getInsightLineage(projectId, basisId, controller.signal)
      .then((lineage) => {
        const nodes = lineage.nodes ?? [];
        const index = nodes.findIndex((node) => node.analysisId === basisId);
        setStale(index >= 0 && index < nodes.length - 1);
      })
      .catch(() => setStale(false));
    return () => controller.abort();
  }, [candidate?.status, candidate?.basis.toAnalysisId, candidate?.basis.fromAnalysisId, client, projectId]);

  const prepare = useCallback(async (options: { force?: boolean } = {}) => {
    const basisFrom = candidate?.basis.fromAnalysisId ?? fromAnalysisId;
    if (!basisFrom) return;
    setBusy(true);
    setError(null);
    try {
      const data = await client.prepareCandidate(projectId, {
        experimentId: experiment.id,
        fromAnalysisId: basisFrom,
        // Rebuild/retry reuses the resolved strategy; first prepare lets the
        // server resolve it from the generation-lockfile pin.
        ...(candidate?.basis.strategyId ? { strategyId: candidate.basis.strategyId } : {}),
        ...(options.force ? { force: true } : {}),
      });
      setCandidate(data.candidate);
      setStale(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Candidate preparation failed');
    } finally {
      setBusy(false);
    }
  }, [client, projectId, experiment.id, candidate?.basis.fromAnalysisId, candidate?.basis.strategyId, fromAnalysisId]);

  const discard = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const data = await client.discardCandidate(projectId, experiment.id);
      setCandidate(data.candidate);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Discard failed');
    } finally {
      setBusy(false);
    }
  }, [client, projectId, experiment.id]);

  /** P3: explicit confirm → server exposure guard → hand off to the existing publish/AB surfaces. */
  const confirmExposure = useCallback(async () => {
    const kind = confirm;
    setConfirm(null);
    setBusy(true);
    setError(null);
    try {
      await client.confirmCandidateExposure(projectId, experiment.id);
      if (kind === 'publish') {
        const funnelId = candidate?.basis.funnelId;
        navigate(funnelId
          ? `/project/${projectId}/onboarding-v2/${funnelId}`
          : `/project/${projectId}/web-placements`);
      } else {
        navigate(`/project/${projectId}/web-ab-tests`);
      }
    } catch (err) {
      if (err instanceof ExperimentsApiError && err.code === 'candidate_stale') {
        setStale(true);
        setError('Insights moved past this candidate — rebuild it before exposing.');
      } else {
        setError(err instanceof Error ? err.message : 'Exposure check failed');
      }
    } finally {
      setBusy(false);
    }
  }, [confirm, client, projectId, experiment.id, candidate?.basis.funnelId, navigate]);

  const viewDiff = useCallback(() => {
    if (!candidate) return;
    const { strategyId, fromAnalysisId: from, toAnalysisId: to } = candidate.basis;
    const params = new URLSearchParams({ impactFrom: from, impactTo: to });
    navigate(`/project/${projectId}/strategy/${strategyId}?${params.toString()}`);
  }, [candidate, navigate, projectId]);

  // Step rows: logged steps keep their status; steps the pipeline skipped
  // (dialog path has no weights/recompute) disappear once it moved past them.
  const stepRows = useMemo(() => {
    const log = candidate?.stepLog ?? [];
    const lastByStep = new Map<CliCandidateStep, { status: string; detail?: string }>();
    for (const entry of log) lastByStep.set(entry.step, entry);
    const furthest = STEP_COPY.reduce((max, step, index) => (lastByStep.has(step.key) ? index : max), -1);
    return STEP_COPY
      .map((step, index) => ({ ...step, index, entry: lastByStep.get(step.key) }))
      .filter((row) => row.entry || row.index > furthest);
  }, [candidate?.stepLog]);

  // AC-D4-5: absent, not disabled — the whole block vanishes under the stop valve.
  if (stopValve) return null;

  const status = candidate?.status;
  const noCandidate = !candidate || status === 'discarded';
  if (noCandidate && !fromAnalysisId) return null;

  return (
    <Box data-testid={EXPERIMENTS_TEST_IDS.candidateBlock(experiment.id)}>
      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

      {noCandidate && (
        <Button
          size="small"
          variant="outlined"
          disabled={busy}
          data-testid={EXPERIMENTS_TEST_IDS.candidatePrepare(experiment.id)}
          onClick={() => prepare()}
        >
          Prepare corrected version
        </Button>
      )}

      {status === 'preparing' && (
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.25 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" sx={{ fontWeight: 600 }}>⏳ Preparing corrected version</Typography>
            <Chip size="small" variant="outlined" color="info" label={`attempt ${candidate!.attempt}`} />
          </Stack>
          <Stack spacing={0.25} sx={{ mt: 0.75 }}>
            {stepRows.map((row, index) => {
              const entryStatus = row.entry?.status;
              const icon = entryStatus === 'done' ? '✓' : entryStatus === 'running' ? '●' : entryStatus === 'error' ? '✕' : '○';
              const color = entryStatus === 'done' ? 'success.main'
                : entryStatus === 'running' ? 'info.main'
                  : entryStatus === 'error' ? 'error.main' : 'text.secondary';
              return (
                <Typography
                  key={row.key}
                  variant="body2"
                  sx={{ color }}
                  data-testid={EXPERIMENTS_TEST_IDS.candidateStep(experiment.id, index)}
                >
                  {icon} {entryStatus === 'done' ? row.done : row.doing}{entryStatus === 'running' ? '…' : ''}
                  {row.entry?.detail && (
                    <Typography component="span" variant="caption" color="text.secondary"> — {row.entry.detail}</Typography>
                  )}
                </Typography>
              );
            })}
          </Stack>
        </Box>
      )}

      {status === 'ready' && !stale && (
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.25 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" sx={{ fontWeight: 600 }}>✅ Corrected version is ready</Typography>
            <Chip size="small" variant="outlined" color="success" label="ready" />
          </Stack>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            Includes:
            {candidate!.weightsApplied.length > 0 ? ` ${candidate!.weightsApplied.length} weight change(s),` : ''}
            {' '}insights recomputed{candidate!.basis.toAnalysisId ? ` (→ ${candidate!.basis.toAnalysisId.slice(0, 8)}…)` : ''},
            {' '}{candidate!.affectedBlocks.length} block(s) rewritten
            {candidate!.result?.funnelVersionId ? `, new onboarding version ${candidate!.result.funnelVersionId.slice(0, 8)}…` : ''}.
          </Typography>
          {candidate!.replacedPolish.length > 0 && (
            <Box
              sx={{ mt: 1, pl: 1.5, py: 0.75, borderLeft: 3, borderColor: 'warning.main', bgcolor: 'action.hover', borderRadius: 1 }}
              data-testid={EXPERIMENTS_TEST_IDS.candidatePolish(experiment.id)}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Your edits in rewritten blocks were replaced:
              </Typography>
              {candidate!.replacedPolish.map((entry) => (
                <Typography key={entry.blockId} variant="body2">
                  • {entry.blockName ?? entry.blockId}
                  {entry.checkpointVersionId && (
                    <Typography component="span" variant="caption" color="text.secondary">
                      {' '}— saved copy in version {entry.checkpointVersionId.slice(0, 8)}…
                    </Typography>
                  )}
                </Typography>
              ))}
              <Typography variant="caption" color="text.secondary">
                Edits in the other blocks are untouched; the saved copies keep your work verbatim.
              </Typography>
            </Box>
          )}
          <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
            <Button size="small" variant="outlined" data-testid={EXPERIMENTS_TEST_IDS.candidateViewDiff(experiment.id)} onClick={viewDiff}>
              View diff
            </Button>
            <Button size="small" variant="outlined" disabled={busy} data-testid={EXPERIMENTS_TEST_IDS.candidateRunAb(experiment.id)} onClick={() => setConfirm('run-ab')}>
              Run A/B
            </Button>
            <Button size="small" variant="contained" disabled={busy} data-testid={EXPERIMENTS_TEST_IDS.candidatePublish(experiment.id)} onClick={() => setConfirm('publish')}>
              Publish
            </Button>
            <Button size="small" disabled={busy} data-testid={EXPERIMENTS_TEST_IDS.candidateDiscard(experiment.id)} onClick={discard}>
              Discard
            </Button>
          </Stack>
        </Box>
      )}

      {status === 'ready' && stale && (
        <Box sx={{ border: 1, borderColor: 'warning.main', borderRadius: 1, p: 1.25 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" sx={{ fontWeight: 600 }}>⚠ Candidate is stale</Typography>
            <Chip
              size="small"
              variant="outlined"
              color="warning"
              label="newer insights available"
              data-testid={EXPERIMENTS_TEST_IDS.candidateStaleChip(experiment.id)}
            />
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Insights changed after this candidate was built. Rebuild to base it on the latest data —
            the current candidate stays until you do.
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Button size="small" variant="contained" disabled={busy} data-testid={EXPERIMENTS_TEST_IDS.candidateRebuild(experiment.id)} onClick={() => prepare({ force: true })}>
              Rebuild
            </Button>
            <Button size="small" endIcon={<OpenInNewIcon fontSize="inherit" />} onClick={viewDiff}>
              What changed
            </Button>
            <Button size="small" disabled={busy} data-testid={EXPERIMENTS_TEST_IDS.candidateDiscard(experiment.id)} onClick={discard}>
              Discard
            </Button>
          </Stack>
        </Box>
      )}

      {status === 'failed' && (
        <Box sx={{ border: 1, borderColor: 'error.main', borderRadius: 1, p: 1.25 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" sx={{ fontWeight: 600 }}>✕ Preparation failed</Typography>
            {candidate!.error && (
              <Chip size="small" variant="outlined" color="error" label={`failed at: ${candidate!.error.step}`} />
            )}
          </Stack>
          {candidate!.error && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {candidate!.error.message} Completed steps are saved — retry continues with a fresh attempt.
            </Typography>
          )}
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Button size="small" variant="contained" disabled={busy} data-testid={EXPERIMENTS_TEST_IDS.candidateRetry(experiment.id)} onClick={() => prepare({ force: true })}>
              Retry
            </Button>
            <Button size="small" disabled={busy} data-testid={EXPERIMENTS_TEST_IDS.candidateDiscard(experiment.id)} onClick={discard}>
              Discard
            </Button>
          </Stack>
        </Box>
      )}

      {status === 'published' && (
        <Chip size="small" variant="outlined" color="success" label="corrected version published" />
      )}

      {/* P3 exposure confirm — the guard runs server-side on accept.
          Destructive confirm: keeps the explicit Cancel (X alone is not a
          decision affordance for a live-traffic switch). */}
      <SDialog
        open={confirm !== null}
        onClose={() => setConfirm(null)}
        data-testid={EXPERIMENTS_TEST_IDS.candidateConfirmDialog}
        title={confirm === 'publish' ? 'Publish corrected version?' : 'Start A/B: current vs corrected?'}
        showCloseButton={false}
        secondaryAction={{
          label: 'Cancel',
          onClick: () => setConfirm(null),
        }}
        primaryAction={{
          label: confirm === 'publish' ? 'Publish to live' : 'Start A/B',
          onClick: confirmExposure,
          color: confirm === 'publish' ? 'error' : 'primary',
          'data-testid': EXPERIMENTS_TEST_IDS.candidateConfirmAccept,
        }}
      >
        <Typography variant="body2" sx={{ px: 3 }}>
          {confirm === 'publish'
            ? 'Live traffic on the linked placement switches to the corrected version. The current version stays frozen — you can switch back anytime.'
            : 'Part of the live traffic will see the corrected version until the test reaches its sample target.'}
        </Typography>
      </SDialog>
    </Box>
  );
};
